import { baseApi } from '../../../shared/api/baseApi';
import type { NotificationDto, NotificationsPageDto, UserNotificationsPageDto } from '../model/dto';
import {
    toNotification,
    toNotificationBody,
    toNotificationsPage,
    toUserNotifications,
} from '../model/mappers';
import type {
    AppNotification,
    NotificationForm,
    NotificationsPage,
    NotificationsQuery,
    UserNotifications,
} from '../model/types';

const LIST = '/api/admin/organizations/get_info_messages';
const ITEM = '/api/admin/organizations/info_message';
const ONE = '/api/admin/organizations/get_info_message';
const EDIT = '/api/admin/organizations/edit_info_message';
const REMOVE = '/api/admin/organizations/delete_info_message';
const USER_LIST = '/api/admin/organizations/get_user_info_messages';

const BELL_PAGE_SIZE = 20;

const TAG = 'Notifications' as const;

export const notificationsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getNotifications: build.query<NotificationsPage, NotificationsQuery>({
            query: ({ pageNum, pageSize, query = '', sortBy, sortOrder }) => ({
                url: LIST,
                params: {
                    page_size: pageSize,
                    page_num: pageNum,
                    query,
                    ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder ?? 'desc' } : {}),
                },
            }),
            transformResponse: (response: NotificationsPageDto) => toNotificationsPage(response),
            providesTags: [{ type: TAG, id: 'LIST' }],
        }),

        getUserNotifications: build.query<UserNotifications, void>({
            query: () => ({
                url: USER_LIST,
                params: { page_size: BELL_PAGE_SIZE, page_num: 1 },
            }),
            transformResponse: (response: UserNotificationsPageDto) => toUserNotifications(response),
            providesTags: [{ type: TAG, id: 'USER' }],
        }),

        getUserNotificationsPage: build.query<
            UserNotifications,
            { pageNum: number; pageSize: number }
        >({
            query: ({ pageNum, pageSize }) => ({
                url: USER_LIST,
                params: { page_size: pageSize, page_num: pageNum },
            }),
            transformResponse: (response: UserNotificationsPageDto) => toUserNotifications(response),
            providesTags: [{ type: TAG, id: 'USER' }],
        }),

        getNotification: build.query<AppNotification, string>({
            query: (id) => `${ONE}/${id}`,
            transformResponse: (response: NotificationDto) => toNotification(response),
            providesTags: (_result, _error, id) => [{ type: TAG, id }],
        }),

        createNotification: build.mutation<unknown, NotificationForm>({
            query: (form) => ({ url: ITEM, method: 'POST', body: toNotificationBody(form) }),
            invalidatesTags: [
                { type: TAG, id: 'LIST' },
                { type: TAG, id: 'USER' },
            ],
        }),

        /* PUT edit_info_message пересоздаёт запись: id меняется, объявление будет
           показано пользователям повторно. Ссылки по старому id перестают работать. */
        updateNotification: build.mutation<unknown, { id: string; form: NotificationForm }>({
            query: ({ id, form }) => ({
                url: `${EDIT}/${id}`,
                method: 'PUT',
                body: toNotificationBody({ ...form, showNow: false }),
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: TAG, id },
                { type: TAG, id: 'LIST' },
                { type: TAG, id: 'USER' },
            ],
        }),

        removeNotification: build.mutation<void, string>({
            query: (id) => ({ url: `${REMOVE}/${id}`, method: 'DELETE' }),
            invalidatesTags: (_result, _error, id) => [
                { type: TAG, id },
                { type: TAG, id: 'LIST' },
                { type: TAG, id: 'USER' },
            ],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetNotificationsQuery,
    useGetUserNotificationsQuery,
    useGetUserNotificationsPageQuery,
    useGetNotificationQuery,
    useCreateNotificationMutation,
    useUpdateNotificationMutation,
    useRemoveNotificationMutation,
} = notificationsApi;